/**
 * Audit Plan Slice (EX9)
 * 
 * Manages audit plan data with offline-first caching pattern:
 * - Applications (Tab 3)
 * - Timeline Controls (Tab 1)
 * - Planning Documents (Tab 2)
 * - Change Indicators (Tab 4)
 * - Certification Messages (Tab 4)
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { 
  Application, 
  TimelineControl, 
  PlanningDocument,
  ChangeIndicator,
  CertificationMessage,
  TimelineCycleGroup,
  CustomPlanTask,
} from '../../services/auditPlanService';
import { resetReduxState } from '../actions';

// ========================================
// State Interface
// ========================================

export interface AuditPlanState {
  // Applications (Tab 3)
  applications: Application[];
  applicationsTotalCount: number;
  applicationsLastFetchedReportId: string | null;
  
  // Timeline Controls (Tab 1)
  timelineControls: TimelineControl[];
  timelineCycleGroups: TimelineCycleGroup[];
  timelineTotalCount: number;
  timelineLastFetchedReportId: string | null;
  
  // Custom Planning Tasks (Tab 1 - top section)
  customPlanningTasks: CustomPlanTask[];
  customPlanningTasksTotalCount: number;
  customPlanningTasksLastFetchedReportId: string | null;
  
  // Planning Documents (Tab 2)
  planningDocuments: PlanningDocument[];
  planningDocumentsTotalCount: number;
  planningDocumentsLastFetchedReportId: string | null;
  
  // Change Indicators (Tab 4)
  changeIndicators: ChangeIndicator[];
  changeIndicatorsTotalCount: number;
  changeIndicatorsLastFetchedReportId: string | null;
  
  // Certification Messages (Tab 4)
  certificationMessages: CertificationMessage[];
  certificationMessagesTotalCount: number;
  certificationMessagesLastFetchedReportId: string | null;
}

const initialState: AuditPlanState = {
  // Applications
  applications: [],
  applicationsTotalCount: 0,
  applicationsLastFetchedReportId: null,
  
  // Timeline Controls
  timelineControls: [],
  timelineCycleGroups: [],
  timelineTotalCount: 0,
  timelineLastFetchedReportId: null,
  
  // Custom Planning Tasks
  customPlanningTasks: [],
  customPlanningTasksTotalCount: 0,
  customPlanningTasksLastFetchedReportId: null,
  
  // Planning Documents
  planningDocuments: [],
  planningDocumentsTotalCount: 0,
  planningDocumentsLastFetchedReportId: null,
  
  // Change Indicators
  changeIndicators: [],
  changeIndicatorsTotalCount: 0,
  changeIndicatorsLastFetchedReportId: null,
  
  // Certification Messages
  certificationMessages: [],
  certificationMessagesTotalCount: 0,
  certificationMessagesLastFetchedReportId: null,
};

// ========================================
// Slice
// ========================================

const auditPlanSlice = createSlice({
  name: 'auditPlan',
  initialState,
  reducers: {
    // ========================================
    // Applications (Tab 3)
    // ========================================
    
    setApplications: (
      state,
      action: PayloadAction<{
        data: Application[];
        totalCount: number;
        reportId: string;
      }>
    ) => {
      state.applications = action.payload.data;
      state.applicationsTotalCount = action.payload.totalCount;
      state.applicationsLastFetchedReportId = action.payload.reportId;
    },
    
    clearApplications: (state) => {
      state.applications = [];
      state.applicationsTotalCount = 0;
      state.applicationsLastFetchedReportId = null;
    },
    
    // ========================================
    // Timeline Controls (Tab 1)
    // ========================================
    
    setTimelineControls: (
      state,
      action: PayloadAction<{
        data: TimelineControl[];
        groupedByCycle: TimelineCycleGroup[];
        totalCount: number;
        reportId: string;
      }>
    ) => {
      state.timelineControls = action.payload.data;
      state.timelineCycleGroups = action.payload.groupedByCycle;
      state.timelineTotalCount = action.payload.totalCount;
      state.timelineLastFetchedReportId = action.payload.reportId;
    },
    
    clearTimelineControls: (state) => {
      state.timelineControls = [];
      state.timelineCycleGroups = [];
      state.timelineTotalCount = 0;
      state.timelineLastFetchedReportId = null;
    },
    
    // ========================================
    // Custom Planning Tasks (Tab 1 - top section)
    // ========================================
    
    setCustomPlanningTasks: (
      state,
      action: PayloadAction<{
        data: CustomPlanTask[];
        totalCount: number;
        reportId: string;
      }>
    ) => {
      state.customPlanningTasks = action.payload.data;
      state.customPlanningTasksTotalCount = action.payload.totalCount;
      state.customPlanningTasksLastFetchedReportId = action.payload.reportId;
    },
    
    clearCustomPlanningTasks: (state) => {
      state.customPlanningTasks = [];
      state.customPlanningTasksTotalCount = 0;
      state.customPlanningTasksLastFetchedReportId = null;
    },
    
    // ========================================
    // Planning Documents (Tab 2)
    // ========================================
    
    setPlanningDocuments: (
      state,
      action: PayloadAction<{
        data: PlanningDocument[];
        totalCount: number;
        reportId: string;
      }>
    ) => {
      state.planningDocuments = action.payload.data;
      state.planningDocumentsTotalCount = action.payload.totalCount;
      state.planningDocumentsLastFetchedReportId = action.payload.reportId;
    },
    
    clearPlanningDocuments: (state) => {
      state.planningDocuments = [];
      state.planningDocumentsTotalCount = 0;
      state.planningDocumentsLastFetchedReportId = null;
    },
    
    // ========================================
    // Change Indicators (Tab 4)
    // ========================================
    
    setChangeIndicators: (
      state,
      action: PayloadAction<{
        data: ChangeIndicator[];
        totalCount: number;
        reportId: string;
      }>
    ) => {
      state.changeIndicators = action.payload.data;
      state.changeIndicatorsTotalCount = action.payload.totalCount;
      state.changeIndicatorsLastFetchedReportId = action.payload.reportId;
    },
    
    clearChangeIndicators: (state) => {
      state.changeIndicators = [];
      state.changeIndicatorsTotalCount = 0;
      state.changeIndicatorsLastFetchedReportId = null;
    },
    
    // ========================================
    // Certification Messages (Tab 4)
    // ========================================
    
    setCertificationMessages: (
      state,
      action: PayloadAction<{
        data: CertificationMessage[];
        totalCount: number;
        reportId: string;
      }>
    ) => {
      state.certificationMessages = action.payload.data;
      state.certificationMessagesTotalCount = action.payload.totalCount;
      state.certificationMessagesLastFetchedReportId = action.payload.reportId;
    },
    
    clearCertificationMessages: (state) => {
      state.certificationMessages = [];
      state.certificationMessagesTotalCount = 0;
      state.certificationMessagesLastFetchedReportId = null;
    },
    
    // ========================================
    // Clear All
    // ========================================
    
    clearAllAuditPlanData: (state) => {
      Object.assign(state, initialState);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetReduxState, () => initialState);
  },
});

export const {
  // Applications
  setApplications,
  clearApplications,
  // Timeline
  setTimelineControls,
  clearTimelineControls,
  // Custom Planning Tasks
  setCustomPlanningTasks,
  clearCustomPlanningTasks,
  // Planning Documents
  setPlanningDocuments,
  clearPlanningDocuments,
  // Change Indicators
  setChangeIndicators,
  clearChangeIndicators,
  // Certification Messages
  setCertificationMessages,
  clearCertificationMessages,
  // Clear All
  clearAllAuditPlanData,
} = auditPlanSlice.actions;

export default auditPlanSlice.reducer;
